import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import type { RefObject } from "react";
import { HELP_SEARCH } from "@/constants/help";

interface HelpSearchBoxProps {
  value: string;
  onChange(value: string): void;
  onClear(): void;
  inputRef: RefObject<HTMLInputElement>;
}

/**
 * The Help Center panel's header search field, matching the design of record's `.hc-search`
 * (content/design/client-experience-prototype.html). Controlled; not wrapped in a form, so Enter
 * submits nothing.
 */
export function HelpSearchBox({ value, onChange, onClear, inputRef }: HelpSearchBoxProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: <search> only maps to the search landmark in 2023+ engines; role="search" on a div is exposed everywhere.
    <div
      role="search"
      className="flex min-w-0 flex-1 items-center gap-2 rounded-[10px] border border-smoke bg-white px-3 py-[7px] transition-colors focus-within:border-dark-dust"
    >
      <SearchIcon aria-hidden="true" className="shrink-0 text-graphite" sx={{ fontSize: 20 }} />
      <input
        ref={inputRef}
        type="search"
        aria-label="Search help articles"
        placeholder="Search help"
        maxLength={HELP_SEARCH.MAX_QUERY_LENGTH}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-[160px] flex-1 border-none bg-transparent py-1 font-lato text-[14px] text-rodeo-black outline-none placeholder:text-graphite placeholder:opacity-100 focus-visible:outline-none [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
      />
      {value !== "" && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear search"
          className="flex shrink-0 cursor-pointer items-center border-0 bg-transparent p-0 text-graphite leading-none hover:text-rodeo-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2"
        >
          <CloseIcon aria-hidden="true" sx={{ fontSize: 18 }} />
        </button>
      )}
    </div>
  );
}

export default HelpSearchBox;
